import { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { TabPanel, TabView } from 'primereact/tabview';

import ChassisDropdown from '../../../../components/common/ChassisDropdown';
import TechnicalServiceDeclarationDropdown from '../../../../components/common/TechnicalServiceDeclarationDropdown';
import ReasonAssistanceRequestDropdown from '../../../../components/common/ReasonAssistanceRequestDropdown';
import TechnicalCurrentStatusDropdown from '../../../../components/common/TechnicalCurrentStatusDropdown';
import TechnicalActionManagerDropdown from '../../../../components/common/TechnicalActionManagerDropdown';
import GenericFileUpload from '../../../../components/common/GenericFileUpload';
import DealerDropdown from '../../../../components/common/DealerDropdown';
import DealerAddressDropdown from '../../../../components/common/DealerAddressDropdown';
import SapCurrencyDropdown from '../../../../components/common/SapCurrencyDropdown';
import FaultCodeDropdowns from './FaultCodeDropdowns';
import FaultModesDropdown from '../../../../components/common/FaultModeDropdown';

import FormSection from './FormSection';
import RadioButtonGroup from './RadioButtonGroup';
import FormField from './FormField';
import ChatHistory from './ChatHistory';
import PolicyDialog from './PolicyDialog';
import WarrantyPolicyDialog from './WarrantyPolicyDialog';
import FeedbackDialog from './FeedbackDialog';
import VehicleWarrantyInfoDialog from './VehicleWarrantyInfoDialog';
import TechnicalSupportRequestHistoryDialog from './TechnicalSupportRequestHistoryDialog';

import useTechnicalSupportRequestFormData from '../hooks/useTechnicalSupportRequestFormData';
import useVehicleWarrantyInfoData from '../hooks/useVehicleWarrantyInfoData';
import useTechnicalSupportRequestHistoryData from '../hooks/useTechnicalSupportRequestHistoryData';
import { STYLES, LONG_TEXT_MAX_LENGTH, ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from '../constants/formConstants';

const TechnicalSupportRequestForm = ({ dialogVisible, editMode, hideDialog, request, onSaveSuccess, onRefreshRequest, isVorMode = false, requireDealerAddressSelection = false }) => {
    const [isChatUploading, setIsChatUploading] = useState(false);
    const {
        th,
        userParams,
        form,
        problemSolvedRef,
        releasedStatusCode,
        changeDateRequired,
        setField,
        handleActionManagerChange,
        handleStatusChange,
        handleProblemSolvedChange,
        handleStatusDateChange,
        scrollToProblemSolved,
        handleSolutionDateChange,
        handleChassisChange,
        setFields,
        isDealer,
        isReadOnly,
        shouldShowDealerAddressSelection,
        isSaving,
        previousKm,
        formattedPreviousKm,
        previousKmUnit,
        isKmBelowPrevious,
        isKmInvalid,
        isFetchingPreviousKm,
        isPreviousKmError,
        isAddingComment,
        isFetchingWarrantyInformation,
        isVehicleWarrantyInfLocked,
        isVehicleModelLocked,
        warrantyOptions,
        gsrOptions,
        menzilTypeOptions,
        errorEffectOptions,
        vehicleEmissionOptions,
        selectedErrorEffectInfo,
        additionalChassisOptions,
        technicalResponsibleOptions,
        isFetchingTechnicalResponsibles,
        selectedSiteUseId,
        canEditTechnicalResponsible,
        isVisitDateAfterErrorNotificationInvalid,
        isSolutionDateAfterErrorNotificationInvalid,
        dealerCustomerFiles,
        technicalTeamFiles,
        documentTabIndex,
        expenseDocuments,
        updatedFiles,
        expenseFiles,
        isLoadingDocuments,
        isDownloadingDocuments,
        isLoadingExpenseDocuments,
        isFetchingAdditionalChassis,
        debouncedChassisSearch,
        feedbackVisible,
        toastRef,
        technicalPolicyRef,
        additionalWarrantyPolicyRef,
        setAdditionalChassisSearch,
        setExpenseFiles,
        setDocumentTabIndex,
        onEditFilesChange,
        onDownloadDocument,
        onDownloadAllDocuments,
        onDeleteDocument,
        onDeleteExpenseDocument,
        handleSubmit,
        handleSendMessage,
        handleFaultCodeChange,
        handleFailureModeChange,
        handleAdditionalChassisChange,
        handleFeedbackSubmit,
        handleFeedbackHide,
        handleWarrantyPolicyAccept
    } = useTechnicalSupportRequestFormData({
        dialogVisible,
        editMode,
        request,
        onSaveSuccess,
        onRefreshRequest,
        isVorMode,
        requireDealerAddressSelection
    });

    const isDialogBusy = isSaving || isChatUploading;
    const warrantyInfo = useVehicleWarrantyInfoData(form.chassisNo, dialogVisible);
    const { requestId: historyRequestId, history, isFetching: isHistoryFetching, isError: isHistoryError, openHistory, closeHistory } = useTechnicalSupportRequestHistoryData();
    const historyButton =
        editMode && request?.id ? (
            <Button type="button" icon="pi pi-info-circle" aria-label={th('technicalSupport.history.title')} tooltip={th('technicalSupport.history.title')} outlined rounded className="w-auto flex-shrink-0" onClick={() => openHistory(request)} />
        ) : null;

    const renderDialogFooter = () => (
        <div className="flex justify-content-end gap-2">
            <Button label={th('technicalSupport.cancel')} icon="pi pi-times" onClick={hideDialog} className="p-button-text" disabled={isDialogBusy} />
            {!isReadOnly && <Button label={th('technicalSupport.save')} icon="pi pi-check" onClick={handleSubmit} loading={isSaving} disabled={isDialogBusy} />}
        </div>
    );

    return (
        <Dialog
            visible={dialogVisible}
            onHide={() => !isDialogBusy && hideDialog()}
            closable={!isDialogBusy}
            header={editMode ? th('technicalSupport.editRequest') : th('technicalSupport.newRequest')}
            style={STYLES.MAIN_DIALOG}
            footer={renderDialogFooter()}
            modal
            className="p-fluid"
            maximizable
        >
            <div className="grid">
                <FormSection title={th('technicalSupport.sections.generalInfo')}>
                    <FormField label={th('technicalSupport.fields.requestCreatedDate')} className="col-12 md:col-4">
                        <InputText id="requestCreatedDate" value={request?.createdDate ? new Date(request.createdDate).toLocaleDateString('tr-TR') : new Date().toLocaleDateString('tr-TR')} disabled={true} />
                    </FormField>

                    <FormField label={th('technicalSupport.fields.errorNotificationDate')} required submitted={form.submitted} error={!form.errorNotificationDate} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-4">
                        <Calendar id="errorNotificationDate" value={form.errorNotificationDate} onChange={(e) => setField('errorNotificationDate', e.value)} dateFormat="dd.mm.yy" showIcon maxDate={new Date()} disabled={isReadOnly} />
                    </FormField>

                    <FormField label={th('technicalSupport.fields.requestNo')} className="col-12 md:col-4">
                        <InputText id="requestNo" value={request?.id || th('technicalSupport.fields.requestNo')} disabled />
                    </FormField>

                    <FormField label={th('technicalSupport.fields.createdBy')} className="col-12 md:col-4">
                        <InputText id="createdBy" value={form.userName || '-'} disabled />
                    </FormField>

                    {shouldShowDealerAddressSelection && (
                        <>
                            <FormField label={th('technicalSupport.fields.dealer')} required submitted={form.submitted} error={!form.selectedDealer} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-4">
                                <DealerDropdown
                                    value={form.selectedDealer}
                                    onChange={(dealer) => {
                                        setFields({
                                            selectedDealer: dealer,
                                            selectedDealerAddress: null,
                                            technicalResponsibleUserId: canEditTechnicalResponsible ? null : form.technicalResponsibleUserId
                                        });
                                    }}
                                    placeholder={th('technicalSupport.selectDealer')}
                                />
                            </FormField>

                            <FormField label={th('userAccounts.address')} required submitted={form.submitted} error={!form.selectedDealerAddress} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-4">
                                <DealerAddressDropdown
                                    value={form.selectedDealerAddress}
                                    onChange={(address) =>
                                        setFields({
                                            selectedDealerAddress: address,
                                            technicalResponsibleUserId: canEditTechnicalResponsible ? null : form.technicalResponsibleUserId
                                        })
                                    }
                                    dealerId={form.selectedDealer?.id || form.selectedDealer?.erpDealerId}
                                    placeholder={th('userAccounts.addressPlaceholder')}
                                    disabled={!form.selectedDealer}
                                />
                            </FormField>

                            <FormField label={th('technicalSupport.columnHeaders.technicalResponsible')} className="col-12 md:col-4">
                                <Dropdown
                                    id="technicalResponsibleUserId"
                                    value={form.technicalResponsibleUserId}
                                    options={technicalResponsibleOptions}
                                    onChange={(e) => setField('technicalResponsibleUserId', Number(e.value))}
                                    placeholder={th('technicalSupport.select')}
                                    emptyMessage={th('technicalSupport.noTechnicalResponsible')}
                                    loading={isFetchingTechnicalResponsibles}
                                    disabled={!selectedSiteUseId || (!isFetchingTechnicalResponsibles && technicalResponsibleOptions.length === 0) || !canEditTechnicalResponsible || isReadOnly}
                                />
                                {selectedSiteUseId && !isFetchingTechnicalResponsibles && technicalResponsibleOptions.length === 0 && <small className="text-600">{th('technicalSupport.noTechnicalResponsible')}</small>}
                            </FormField>
                        </>
                    )}
                </FormSection>

                <FormSection title={th('technicalSupport.sections.vehicleInfo')}>
                    <FormField label={th('technicalSupport.fields.chassisNo')} required submitted={form.submitted} error={!form.chassisNo} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-4">
                        <div className="flex align-items-center gap-2 w-full flex-nowrap">
                            <div className="flex-1 min-w-0">
                                <ChassisDropdown value={form.chassisNo} onChange={handleChassisChange} disabled={isReadOnly} className="w-full" />
                            </div>
                            {form.chassisNo && (
                                <Button
                                    type="button"
                                    icon="pi pi-info-circle"
                                    className="p-button-outlined p-button-rounded w-auto flex-shrink-0"
                                    aria-label={th('technicalSupport.warrantyInfoTooltip')}
                                    tooltip={th('technicalSupport.warrantyInfoTooltip')}
                                    onClick={warrantyInfo.open}
                                />
                            )}
                        </div>
                    </FormField>

                    <FormField label={th('technicalSupport.fields.vehicleWarrantyInf')} required submitted={form.submitted} error={!form.vehicleWarrantyInf} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-4">
                        <div className="flex align-items-center gap-2 w-full">
                            <Dropdown
                                id="vehicleWarrantyInf"
                                value={form.vehicleWarrantyInf}
                                options={warrantyOptions}
                                onChange={(e) => setField('vehicleWarrantyInf', e.value)}
                                placeholder={th('technicalSupport.select')}
                                disabled={isVehicleWarrantyInfLocked || isFetchingWarrantyInformation || isReadOnly}
                                className="flex-1"
                            />
                        </div>
                    </FormField>

                    <FormField
                        label={th('technicalSupport.fields.vehicleModelEmissions')}
                        required
                        submitted={form.submitted}
                        error={!form.vehicleModelEmissions?.trim()}
                        errorMessage={th('technicalSupport.requiredField')}
                        className="col-12 md:col-4"
                    >
                        <InputText id="vehicleModelEmissions" value={form.vehicleModelEmissions} onChange={(e) => setField('vehicleModelEmissions', e.target.value)} disabled={isVehicleModelLocked || isReadOnly} />
                    </FormField>

                    <FormField label={th('technicalSupport.fields.modelYear')} className="col-12 md:col-4">
                        <InputText id="modelYear" value={form.modelYear ?? ''} disabled />
                    </FormField>

                    <FormField
                        label={th('technicalSupport.fields.vehicleEmissions')}
                        required
                        submitted={form.submitted}
                        error={form.vehicleEmissions === null || form.vehicleEmissions === undefined}
                        errorMessage={th('technicalSupport.requiredField')}
                        className="col-12 md:col-4"
                    >
                        <Dropdown id="vehicleEmissions" value={form.vehicleEmissions} options={vehicleEmissionOptions} onChange={(e) => setField('vehicleEmissions', e.value)} placeholder={th('technicalSupport.select')} disabled={isReadOnly} />
                    </FormField>

                    <FormField
                        label={th('technicalSupport.fields.vehicleGsr')}
                        required
                        submitted={form.submitted}
                        error={form.vehicleGsr === null || form.vehicleGsr === undefined}
                        errorMessage={th('technicalSupport.requiredField')}
                        className="col-12 md:col-4"
                    >
                        <Dropdown id="vehicleGsr" value={form.vehicleGsr} options={gsrOptions} onChange={(e) => setField('vehicleGsr', e.value)} placeholder={th('technicalSupport.select')} disabled={isReadOnly} />
                    </FormField>

                    <FormField
                        label={th('technicalSupport.fields.kmInformation')}
                        required
                        submitted={form.submitted}
                        error={!form.menzilType || form.kmInformation === null || form.kmInformation === undefined || isKmInvalid || isKmBelowPrevious}
                        errorMessage={isKmBelowPrevious ? th('technicalSupport.previousKmError', { km: formattedPreviousKm, unit: previousKmUnit }) : isKmInvalid ? th('technicalSupport.kmMustBeInteger') : th('technicalSupport.requiredField')}
                        className="col-12 md:col-4"
                    >
                        <div className="flex align-items-center gap-2 w-full flex-nowrap">
                            <InputNumber id="kmInformation" value={form.kmInformation} onValueChange={(e) => setField('kmInformation', e.value)} locale="en-US" maxFractionDigits={0} className="w-full" disabled={isReadOnly} />
                            <Dropdown id="menzilType" value={form.menzilType} options={menzilTypeOptions} onChange={(e) => setField('menzilType', e.value)} style={{ minWidth: '7rem' }} disabled={isReadOnly} />
                        </div>
                        {previousKm !== null && <small className="block text-500 mt-1">{th('technicalSupport.previousKm', { km: formattedPreviousKm, unit: previousKmUnit })}</small>}
                        {form.chassisNo && isFetchingPreviousKm && <small className="block text-500 mt-1">{th('common.loading')}</small>}
                        {form.chassisNo && isPreviousKmError && <small className="block p-error mt-1">{th('technicalSupport.previousKmUnavailable')}</small>}
                        {isKmBelowPrevious && !form.submitted && <small className="block p-error mt-1">{th('technicalSupport.previousKmError', { km: formattedPreviousKm, unit: previousKmUnit })}</small>}
                    </FormField>

                    <FormField label={th('technicalSupport.fields.vehicleCustomerInf')} required submitted={form.submitted} error={!form.vehicleCustomerInf?.trim()} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-4">
                        <InputText id="vehicleCustomerInf" value={form.vehicleCustomerInf} onChange={(e) => setField('vehicleCustomerInf', e.target.value)} disabled={isReadOnly} />
                    </FormField>
                </FormSection>

                <FormSection title={th('technicalSupport.sections.errorDetails')}>
                    <FormField label={th('technicalSupport.fields.errorDescription')} required submitted={form.submitted} error={!form.errorDescription.trim()} errorMessage={th('technicalSupport.requiredField')} className="col-12">
                        <InputTextarea id="errorDescription" value={form.errorDescription} onChange={(e) => setField('errorDescription', e.target.value)} rows={4} maxLength={LONG_TEXT_MAX_LENGTH} disabled={isReadOnly} />
                        <small className="block text-right text-500 mt-1">
                            {form.errorDescription.length} / {LONG_TEXT_MAX_LENGTH}
                        </small>
                    </FormField>

                    <FormField label={th('technicalSupport.fields.checksPerformed')} required submitted={form.submitted} error={!form.checksPerformed?.trim()} errorMessage={th('technicalSupport.requiredField')} className="col-12">
                        <InputTextarea id="checksPerformed" value={form.checksPerformed} onChange={(e) => setField('checksPerformed', e.target.value)} rows={4} maxLength={LONG_TEXT_MAX_LENGTH} disabled={isReadOnly} />
                        <small className="block text-right text-500 mt-1">
                            {(form.checksPerformed || '').length} / {LONG_TEXT_MAX_LENGTH}
                        </small>
                    </FormField>

                    <div className="col-12">
                        <div className="bg-primary text-white p-3 border-round-top font-bold text-lg mb-0">{th('technicalSupport.faultCodes.title')}</div>
                        <div className="surface-ground border-1 border-200 border-round-bottom p-4 border-top-none">
                            <div className="grid">
                                <FaultCodeDropdowns
                                    mainCategoryValue={form.mainCategory}
                                    secondCategoryValue={form.secondCategory}
                                    subCategoryValue={form.subCategory}
                                    subPartCategoryValue={form.subPartCategory}
                                    onMainCategoryChange={(value) => handleFaultCodeChange('mainCategory', value)}
                                    onSecondCategoryChange={(value) => handleFaultCodeChange('secondCategory', value)}
                                    onSubCategoryChange={(value) => handleFaultCodeChange('subCategory', value)}
                                    onSubPartCategoryChange={(value) => handleFaultCodeChange('subPartCategory', value)}
                                    showValidation={form.submitted}
                                    mainCategoryError={form.submitted && !form.mainCategory}
                                    secondCategoryError={form.submitted && !form.secondCategory}
                                    mainCategoryErrorMessage={th('technicalSupport.validation.faultMainSystemRequired')}
                                    secondCategoryErrorMessage={th('technicalSupport.validation.faultSecondarySystemRequired')}
                                    disabled={isReadOnly}
                                />

                                <div className="col-12" style={{ flex: '0 0 20%', maxWidth: '20%' }}>
                                    <label className="block font-bold mb-2">{th('technicalSupport.faultCodes.failureMode')} *</label>
                                    <FaultModesDropdown
                                        value={form.failureMode}
                                        onChange={handleFailureModeChange}
                                        className={form.submitted && !form.failureMode ? 'p-invalid w-full' : 'w-full'}
                                        placeholder={th('technicalSupport.placeholders.failureMode')}
                                        disabled={isReadOnly}
                                    />
                                    {form.submitted && !form.failureMode && <small className="p-error">{th('technicalSupport.validation.faultFailureModeRequired')}</small>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <FormField label={th('technicalSupport.fields.errorEffect')} required submitted={form.submitted} error={!form.errorEffect} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-6">
                        <Dropdown id="errorEffect" value={form.errorEffect} options={errorEffectOptions} onChange={(e) => setField('errorEffect', e.value)} placeholder={th('technicalSupport.select')} disabled={isReadOnly} />
                    </FormField>
                    <div className="col-12 md:col-6 flex align-items-end">
                        {!!form.errorEffect && !!selectedErrorEffectInfo && (
                            <div className="text-600 text-sm flex align-items-center gap-1 mb-2">
                                <i className="pi pi-info-circle" />
                                <span>{selectedErrorEffectInfo}</span>
                            </div>
                        )}
                    </div>

                    <div className="col-12">
                        <div className="field-checkbox">
                            <Checkbox inputId="isVor" checked={form.isVor} onChange={(e) => setField('isVor', e.checked)} disabled={isVorMode || isReadOnly} />
                            <label htmlFor="isVor" className="ml-2 font-bold">
                                {th('technicalSupport.fields.isVor')}
                            </label>
                        </div>
                        {form.isVor && (
                            <div className="text-600 text-sm mt-2 ml-4">
                                <i className="pi pi-info-circle mr-2" />
                                <span>{th('technicalSupport.errorEffectInfo.2')}</span>
                            </div>
                        )}
                    </div>

                    <RadioButtonGroup
                        label={th('technicalSupport.fields.moreChassis')}
                        value={form.moreChassis}
                        onChange={(value) => setField('moreChassis', value)}
                        required
                        submitted={form.submitted}
                        errorMessage={th('technicalSupport.requiredField')}
                        idPrefix="moreChassis"
                        disabled={isReadOnly}
                    />

                    {form.moreChassis && (
                        <FormField label={th('technicalSupport.fields.additionalChassis')} required submitted={form.submitted} error={(form.additionalChassis || []).length === 0} errorMessage={th('technicalSupport.requiredField')} className="col-12">
                            <MultiSelect
                                value={form.additionalChassis}
                                options={additionalChassisOptions}
                                onChange={(e) => handleAdditionalChassisChange(e.value)}
                                onFilter={(e) => setAdditionalChassisSearch(e.filter)}
                                placeholder={th('technicalSupport.selectChassis')}
                                filter
                                showClear
                                display="chip"
                                className="w-full"
                                emptyFilterMessage={isFetchingAdditionalChassis ? th('common.loading') : debouncedChassisSearch.length < 2 ? th('technicalSupport.typeToSearch') : 'No results found'}
                                disabled={isReadOnly}
                            />
                        </FormField>
                    )}
                    {form.moreChassis && form.additionalChassisHasOutOfWarranty && !isReadOnly && (
                        <div className="col-12" ref={additionalWarrantyPolicyRef}>
                            <div className="field">
                                <div className={`field-checkbox flex align-items-center p-2 border-round ${form.submitted && !form.additionalChassisWarrantyPolicyAccepted ? 'border-1 border-red-500 bg-red-50' : ''}`}>
                                    <Checkbox
                                        inputId="additionalWarrantyPolicy"
                                        checked={form.additionalChassisWarrantyPolicyAccepted}
                                        onChange={(e) => {
                                            if (e.checked && !form.additionalChassisWarrantyPolicyAccepted) {
                                                setFields({
                                                    hasScrolledToBottomWarranty: false,
                                                    warrantyPolicyDialogVisible: true,
                                                    warrantyPolicyDialogContext: 'additional'
                                                });
                                            } else if (!e.checked) {
                                                setField('additionalChassisWarrantyPolicyAccepted', false);
                                            }
                                        }}
                                    />
                                    <label htmlFor="additionalWarrantyPolicy" className={`ml-2 font-bold ${form.submitted && !form.additionalChassisWarrantyPolicyAccepted ? 'text-red-500' : ''}`} style={{ fontSize: '1.12rem' }}>
                                        {th('technicalSupport.fields.technicalPolicy')}
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    <FormField
                        label={th('technicalSupport.fields.softwareVersion')}
                        required
                        submitted={form.submitted}
                        error={!form.cluster?.trim() || !form.evcu?.trim() || !form.zr5?.trim()}
                        errorMessage={th('technicalSupport.requiredField')}
                        className="col-12"
                    >
                        <div className="grid">
                            <div className="col-12 md:col-4">
                                <InputText
                                    id="cluster"
                                    value={form.cluster}
                                    onChange={(e) => setField('cluster', e.target.value)}
                                    placeholder={th('technicalSupport.fields.cluster')}
                                    className={!form.cluster?.trim() && form.submitted ? 'p-invalid' : ''}
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div className="col-12 md:col-4">
                                <InputText
                                    id="evcu"
                                    value={form.evcu}
                                    onChange={(e) => setField('evcu', e.target.value)}
                                    placeholder={th('technicalSupport.fields.evcu')}
                                    className={!form.evcu?.trim() && form.submitted ? 'p-invalid' : ''}
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div className="col-12 md:col-4">
                                <InputText
                                    id="zr5"
                                    value={form.zr5}
                                    onChange={(e) => setField('zr5', e.target.value)}
                                    placeholder={th('technicalSupport.fields.zr5')}
                                    className={!form.zr5?.trim() && form.submitted ? 'p-invalid' : ''}
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                    </FormField>
                </FormSection>

                <RadioButtonGroup label={th('technicalSupport.fields.problemSolved')} value={form.problemSolved} onChange={handleProblemSolvedChange} idPrefix="problemSolved" disabled={isReadOnly} containerRef={problemSolvedRef} />

                {form.problemSolved && (
                    <>
                        <FormField label={th('technicalSupport.fields.solutionMethod')} required submitted={form.submitted} error={!form.solutionMethod.trim()} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-8">
                            <InputTextarea id="solutionMethod" value={form.solutionMethod} onChange={(e) => setField('solutionMethod', e.target.value)} rows={3} maxLength={LONG_TEXT_MAX_LENGTH} disabled={isReadOnly} />
                            <small className="block text-right text-500 mt-1">
                                {(form.solutionMethod || '').length} / {LONG_TEXT_MAX_LENGTH}
                            </small>
                        </FormField>

                        <FormField
                            label={th('technicalSupport.fields.solutionDate')}
                            required
                            submitted={form.submitted || isSolutionDateAfterErrorNotificationInvalid}
                            error={!form.solutionDate || isSolutionDateAfterErrorNotificationInvalid}
                            errorMessage={isSolutionDateAfterErrorNotificationInvalid ? th('technicalSupport.validation.solutionDateAfterErrorNotification') : th('technicalSupport.requiredField')}
                            className="col-12 md:col-4"
                        >
                            <Calendar id="solutionDate" value={form.solutionDate} onChange={(e) => handleSolutionDateChange(e.value)} dateFormat="dd.mm.yy" showIcon maxDate={new Date()} disabled={isReadOnly || form.solutionDateFromStatus} />
                        </FormField>
                    </>
                )}

                {!isVorMode && (
                    <FormSection title={th('technicalSupport.sections.technicalSupportRequest')}>
                        <RadioButtonGroup label={th('technicalSupport.fields.onSiteSupport')} value={form.onSiteSupport} onChange={(value) => setField('onSiteSupport', value)} idPrefix="onSiteSupport" disabled={isReadOnly} />

                        {form.onSiteSupport && (
                            <>
                                <FormField label={th('technicalSupport.fields.technicalServiceDeclaration')} className="col-12 md:col-6">
                                    <TechnicalServiceDeclarationDropdown value={form.technicalServiceDeclaration} onChange={(v) => setField('technicalServiceDeclaration', v)} placeholder={th('technicalSupport.select')} disabled={isReadOnly} />
                                </FormField>

                                <FormField label={th('technicalSupport.fields.onSiteSupportReason')} className="col-12 md:col-6">
                                    <ReasonAssistanceRequestDropdown value={form.onSiteSupportReason} onChange={(v) => setField('onSiteSupportReason', v)} placeholder={th('technicalSupport.select')} disabled={isReadOnly} />
                                </FormField>

                                <div className="col-12" ref={technicalPolicyRef}>
                                    <div className="field">
                                        <div className={`field-checkbox flex align-items-center p-2 border-round ${form.submitted && form.onSiteSupport && !form.technicalPolicy ? 'border-1 border-red-500 bg-red-50' : ''}`}>
                                            <Checkbox
                                                inputId="technicalPolicy"
                                                checked={form.technicalPolicy}
                                                onChange={(e) => {
                                                    if (e.checked && !form.technicalPolicy) {
                                                        setField('hasScrolledToBottom', false);
                                                        setField('policyDialogVisible', true);
                                                    }
                                                }}
                                                disabled={form.technicalPolicy || isReadOnly}
                                            />
                                            <label htmlFor="technicalPolicy" className="ml-2 font-bold" style={{ fontSize: '1.12rem' }}>
                                                {th('technicalSupport.fields.technicalPolicy')}
                                            </label>
                                        </div>
                                        {form.submitted && form.onSiteSupport && !form.technicalPolicy}
                                    </div>
                                </div>

                                {!isDealer && (
                                    <>
                                        <FormField
                                            label={th('technicalSupport.fields.visitDate')}
                                            required={form.submitted || isVisitDateAfterErrorNotificationInvalid}
                                            submitted={form.submitted || isVisitDateAfterErrorNotificationInvalid}
                                            error={(form.submitted && !form.visitDate) || isVisitDateAfterErrorNotificationInvalid}
                                            errorMessage={isVisitDateAfterErrorNotificationInvalid ? th('technicalSupport.validation.visitDateAfterErrorNotification') : th('technicalSupport.requiredField')}
                                            className="col-12 md:col-6"
                                        >
                                            <Calendar id="visitDate" value={form.visitDate} onChange={(e) => setField('visitDate', e.value)} dateFormat="dd.mm.yy" showIcon />
                                        </FormField>

                                        <FormField
                                            label={th('technicalSupport.fields.visitErrorDesc')}
                                            required={form.submitted}
                                            submitted={form.submitted}
                                            error={form.submitted && !form.visitErrorDesc?.trim()}
                                            errorMessage={th('technicalSupport.requiredField')}
                                            className="col-12"
                                        >
                                            <InputTextarea id="visitErrorDesc" value={form.visitErrorDesc} onChange={(e) => setField('visitErrorDesc', e.target.value)} rows={3} />
                                        </FormField>

                                        <FormField
                                            label={th('technicalSupport.fields.rootCause')}
                                            required={form.submitted}
                                            submitted={form.submitted}
                                            error={form.submitted && !form.rootCause?.trim()}
                                            errorMessage={th('technicalSupport.requiredField')}
                                            className="col-12 md:col-6"
                                        >
                                            <InputText id="rootCause" value={form.rootCause} onChange={(e) => setField('rootCause', e.target.value)} />
                                        </FormField>

                                        <FormField
                                            label={th('technicalSupport.fields.solution')}
                                            required={form.submitted}
                                            submitted={form.submitted}
                                            error={form.submitted && !form.solution?.trim()}
                                            errorMessage={th('technicalSupport.requiredField')}
                                            className="col-12 md:col-6"
                                        >
                                            <InputText id="solution" value={form.solution} onChange={(e) => setField('solution', e.target.value)} />
                                        </FormField>

                                        <FormField
                                            label={th('technicalSupport.fields.manHour')}
                                            required={form.submitted}
                                            submitted={form.submitted}
                                            error={form.submitted && (form.manHour === null || form.manHour === undefined)}
                                            errorMessage={th('technicalSupport.requiredField')}
                                            className="col-12 md:col-3"
                                        >
                                            <InputNumber id="manHour" value={form.manHour} onValueChange={(e) => setField('manHour', e.value)} />
                                        </FormField>

                                        <FormField label={th('technicalSupport.fields.outsourcingExpense')} className="col-12 md:col-3">
                                            <InputNumber id="outsourcingExpense" value={form.outsourcingExpense} onValueChange={(e) => setField('outsourcingExpense', e.value)} />
                                        </FormField>

                                        <FormField
                                            label={th('technicalSupport.fields.totalExpenses')}
                                            required={form.submitted}
                                            submitted={form.submitted}
                                            error={form.submitted && (form.totalExpenses === null || form.totalExpenses === undefined)}
                                            errorMessage={th('technicalSupport.requiredField')}
                                            className="col-12 md:col-3"
                                        >
                                            <InputNumber id="totalExpenses" value={form.totalExpenses} onValueChange={(e) => setField('totalExpenses', e.value)} />
                                        </FormField>

                                        <FormField
                                            label={th('technicalSupport.fields.expenseCurr')}
                                            required={form.submitted && (form.outsourcingExpense !== null || form.totalExpenses !== null)}
                                            submitted={form.submitted}
                                            error={form.submitted && (form.outsourcingExpense !== null || form.totalExpenses !== null) && !form.expenseCurr}
                                            errorMessage={th('technicalSupport.requiredField')}
                                            className="col-12 md:col-3"
                                        >
                                            <SapCurrencyDropdown value={form.expenseCurr} onChange={(e) => setField('expenseCurr', e.value)} isDealer={isDealer} placeholder={th('technicalSupport.select')} />
                                        </FormField>

                                        <FormField label={th('technicalSupport.fields.expenseDocuments')} className="col-12">
                                            <GenericFileUpload
                                                key={dialogVisible ? 'expense-open' : 'expense-closed'}
                                                newFiles={expenseFiles}
                                                onFilesChange={(files) => setExpenseFiles(files || [])}
                                                existingDocuments={expenseDocuments}
                                                onDownloadDocument={onDownloadDocument}
                                                onDeleteExistingDocument={editMode ? onDeleteExpenseDocument : null}
                                                isLoading={isLoadingExpenseDocuments}
                                                multiple={true}
                                                showDownloadButton={true}
                                                showUpdateButton={false}
                                                showDeleteButton={editMode}
                                                maxFileSize={MAX_FILE_SIZE}
                                                acceptedFileTypes={ACCEPTED_FILE_TYPES}
                                            />
                                        </FormField>
                                    </>
                                )}
                            </>
                        )}
                    </FormSection>
                )}

                {!isDealer && (
                    <FormSection title={th('technicalSupport.sections.ptsCriteria')}>
                        <RadioButtonGroup label={th('technicalSupport.fields.ptsCriteria')} value={form.ptsCriteria} onChange={(value) => setField('ptsCriteria', value)} idPrefix="ptsCriteria" />

                        {form.ptsCriteria && (
                            <FormField
                                label={th('technicalSupport.fields.ptsNumber')}
                                required
                                submitted={form.submitted}
                                error={form.submitted && !form.ptsNumber?.trim()}
                                errorMessage={th('technicalSupport.requiredField')}
                                className="col-12 md:col-6"
                            >
                                <InputText id="ptsNumber" value={form.ptsNumber} onChange={(e) => setField('ptsNumber', e.target.value)} />
                            </FormField>
                        )}
                    </FormSection>
                )}

                {(!isDealer || form.statusChanged) && (
                    <FormSection title={th('technicalSupport.sections.otherInfo')}>
                        {!isDealer && (
                            <>
                                <FormField
                                    label={th('technicalSupport.fields.statusNotification')}
                                    required={form.statusChanged}
                                    submitted={form.submitted}
                                    error={form.statusNotification === null || form.statusNotification === undefined}
                                    errorMessage={th('technicalSupport.requiredField')}
                                    className="col-12 md:col-4"
                                >
                                    <div className="flex align-items-center gap-2">
                                        {historyButton}
                                        <div className="flex-1" style={{ minWidth: 0 }}>
                                            <TechnicalCurrentStatusDropdown value={form.statusNotification} onChange={handleStatusChange} placeholder={th('technicalSupport.select')} autoSelectFirst={true} />
                                        </div>
                                    </div>
                                </FormField>

                                <FormField label={th('technicalSupport.fields.actionManager')} className="col-12 md:col-4">
                                    <TechnicalActionManagerDropdown value={form.actionManager} onChange={handleActionManagerChange} placeholder={th('technicalSupport.select')} autoSelectFirst={true} />
                                </FormField>
                            </>
                        )}

                        <FormField label={th('technicalSupport.fields.statusDate')} required={changeDateRequired} submitted={form.submitted} error={!form.statusDate} errorMessage={th('technicalSupport.requiredField')} className="col-12 md:col-4">
                            <div className="flex align-items-center gap-2">
                                {isDealer && historyButton}
                                <Calendar
                                    id="statusDate"
                                    value={form.statusDate}
                                    onChange={(e) => handleStatusDateChange(e.value)}
                                    onHide={scrollToProblemSolved}
                                    dateFormat="dd.mm.yy"
                                    showIcon
                                    className="flex-1"
                                    style={{ minWidth: 0 }}
                                    disabled={isReadOnly || !changeDateRequired || (form.problemSolved && !form.solutionDateFromStatus && form.statusNotification === releasedStatusCode && !form.actionManagerChanged)}
                                />
                            </div>
                        </FormField>
                    </FormSection>
                )}

                <FormSection title={th('technicalSupport.sections.documents')}>
                    <div className="col-12">
                        <GenericFileUpload
                            key={dialogVisible ? 'document-upload-open' : 'document-upload-closed'}
                            newFiles={updatedFiles}
                            onFilesChange={onEditFilesChange}
                            multiple={true}
                            maxFileSize={MAX_FILE_SIZE}
                            acceptedFileTypes={ACCEPTED_FILE_TYPES}
                            disabled={isReadOnly}
                        />

                        <TabView activeIndex={documentTabIndex} onTabChange={(event) => setDocumentTabIndex(event.index)}>
                            <TabPanel header={th('technicalSupport.documentTabs.technicalTeam')}>
                                {editMode && technicalTeamFiles.length > 0 && (
                                    <div className="flex justify-content-end mb-3">
                                        <Button
                                            type="button"
                                            label={th('technicalSupport.downloadAll')}
                                            icon="pi pi-download"
                                            className="p-button-sm p-button-outlined w-auto"
                                            onClick={() => onDownloadAllDocuments('TechnicalTeam')}
                                            loading={isDownloadingDocuments}
                                            disabled={isDownloadingDocuments}
                                        />
                                    </div>
                                )}
                                <GenericFileUpload
                                    existingDocuments={technicalTeamFiles}
                                    onDownloadDocument={onDownloadDocument}
                                    onDeleteExistingDocument={editMode ? onDeleteDocument : null}
                                    isLoading={isLoadingDocuments}
                                    showDownloadButton={true}
                                    showUpdateButton={false}
                                    showDeleteButton={editMode}
                                    disabled={true}
                                />
                                {!isLoadingDocuments && technicalTeamFiles.length === 0 && <div className="text-center text-color-secondary p-4 border-1 surface-border border-round">{th('common.noRecords')}</div>}
                            </TabPanel>
                            <TabPanel header={th('technicalSupport.documentTabs.dealerCustomer')}>
                                {editMode && dealerCustomerFiles.length > 0 && (
                                    <div className="flex justify-content-end mb-3">
                                        <Button
                                            type="button"
                                            label={th('technicalSupport.downloadAll')}
                                            icon="pi pi-download"
                                            className="p-button-sm p-button-outlined w-auto"
                                            onClick={() => onDownloadAllDocuments('Dealer')}
                                            loading={isDownloadingDocuments}
                                            disabled={isDownloadingDocuments}
                                        />
                                    </div>
                                )}
                                <GenericFileUpload
                                    existingDocuments={dealerCustomerFiles}
                                    onDownloadDocument={onDownloadDocument}
                                    onDeleteExistingDocument={editMode ? onDeleteDocument : null}
                                    isLoading={isLoadingDocuments}
                                    showDownloadButton={true}
                                    showUpdateButton={false}
                                    showDeleteButton={editMode}
                                    disabled={true}
                                />
                                {!isLoadingDocuments && dealerCustomerFiles.length === 0 && <div className="text-center text-color-secondary p-4 border-1 surface-border border-round">{th('common.noRecords')}</div>}
                            </TabPanel>
                        </TabView>
                    </div>
                </FormSection>

                {editMode && request?.id && (
                    <ChatHistory
                        chatHistory={form.chatHistory}
                        ticketId={request.id}
                        newMessage={form.newMessage}
                        onMessageChange={(value) => setField('newMessage', value)}
                        onSendMessage={handleSendMessage}
                        isSending={isAddingComment}
                        currentUserEmail={userParams?.userEmail}
                        currentUserId={userParams?.userId}
                        onRefreshRequest={onRefreshRequest}
                        onUploadStateChange={setIsChatUploading}
                        disabled={isReadOnly}
                    />
                )}
            </div>

            <Toast ref={toastRef} />

            <FeedbackDialog visible={feedbackVisible} onHide={handleFeedbackHide} onSubmit={handleFeedbackSubmit} />
            <VehicleWarrantyInfoDialog visible={warrantyInfo.visible} onHide={warrantyInfo.hide} chassisNo={form.chassisNo} items={warrantyInfo.items} isLoading={warrantyInfo.isLoading} isError={warrantyInfo.isError} />
            <TechnicalSupportRequestHistoryDialog requestId={historyRequestId} history={history} isFetching={isHistoryFetching} isError={isHistoryError} onHide={closeHistory} />

            <PolicyDialog
                visible={form.policyDialogVisible}
                onHide={() => setField('policyDialogVisible', false)}
                hasScrolledToBottom={form.hasScrolledToBottom}
                onScrollChange={(value) => setField('hasScrolledToBottom', value)}
                onPolicyAccept={() => setFields({ technicalPolicy: true, policyDialogVisible: false })}
            />
            <WarrantyPolicyDialog
                visible={form.warrantyPolicyDialogVisible}
                onHide={() => setField('warrantyPolicyDialogVisible', false)}
                hasScrolledToBottom={form.hasScrolledToBottomWarranty}
                onScrollChange={(value) => setField('hasScrolledToBottomWarranty', value)}
                accepted={form.warrantyPolicyDialogContext === 'main' ? form.mainChassisPolicy === 1 : form.additionalChassisWarrantyPolicyAccepted}
                onPolicyAccept={handleWarrantyPolicyAccept}
            />
        </Dialog>
    );
};

export default TechnicalSupportRequestForm;
